import React, { useState } from "react";
import { LayoutDashboard, Inbox, FolderOpen, Settings, BarChart2, Target, ChevronRight } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV_ITEMS = [
  { id: "dashboard", Icon: LayoutDashboard, label: "Dashboard" },
  { id: "inbox",     Icon: Inbox,           label: "Inbox"     },
  { id: "projects",  Icon: FolderOpen,      label: "Projects"  },
  { id: "goals",     Icon: Target,          label: "Goals"     },
  { id: "stats",     Icon: BarChart2,       label: "Stats"     },
  { id: "settings",  Icon: Settings,        label: "Settings"  },
];

/* ── Standard desktop sidebar item ── */
function SidebarItem({ id, Icon, label, active, open, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      data-testid={`nav-${id}`}
      onClick={onClick}
      title={!open ? label : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: open ? "flex-start" : "center",
        gap: open ? "12px" : "0",
        width: "100%",
        padding: open ? "11px 20px" : "11px 0",
        background: active ? "rgba(26,77,46,0.12)" : hover ? "rgba(26,77,46,0.07)" : "transparent",
        border: "none",
        borderLeft: active ? "3px solid #1A4D2E" : "3px solid transparent",
        color: active ? "#1A4D2E" : "#374151",
        fontSize: "0.875rem",
        fontFamily: "'Manrope', sans-serif",
        fontWeight: active ? 700 : 500,
        cursor: "pointer",
        textAlign: "left",
        transition: "background 0.15s, color 0.15s",
        whiteSpace: "nowrap",
      }}
    >
      <Icon size={17} />
      {open && <span>{label}</span>}
    </button>
  );
}

/* ── Projects item — Shadcn DropdownMenu floating panel ── */
function ProjectsDropdown({ open, active, projects = [], onNavigate, onOpenKanban }) {
  const [hover, setHover] = useState(false);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="nav-projects"
          title={!open ? "Projects" : undefined}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: open ? "flex-start" : "center",
            gap: open ? "12px" : "0",
            width: "100%",
            padding: open ? "11px 20px" : "11px 0",
            background: active ? "rgba(26,77,46,0.12)" : hover ? "rgba(26,77,46,0.07)" : "transparent",
            border: "none",
            borderLeft: active ? "3px solid #1A4D2E" : "3px solid transparent",
            color: active ? "#1A4D2E" : "#374151",
            fontSize: "0.875rem",
            fontFamily: "'Manrope', sans-serif",
            fontWeight: active ? 700 : 500,
            cursor: "pointer",
            textAlign: "left",
            transition: "background 0.15s, color 0.15s",
            whiteSpace: "nowrap",
            outline: "none",
          }}
        >
          <FolderOpen size={17} />
          {open && (
            <>
              <span style={{ flex: 1 }}>Projects</span>
              <ChevronRight size={13} style={{ opacity: 0.5, marginRight: "4px" }} />
            </>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side="right"
        align="start"
        sideOffset={6}
        className="w-56 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-left-4 data-[state=open]:slide-in-from-left-4 duration-200"
        style={{
          fontFamily: "'Manrope', sans-serif",
          boxShadow: "0 8px 30px rgba(26,77,46,0.15)",
          border: "1px solid rgba(26,77,46,0.12)",
        }}
      >
        <DropdownMenuLabel
          style={{
            fontFamily: "'Manrope', sans-serif",
            color: "#1A4D2E",
            fontSize: "0.75rem",
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
          }}
        >
          Projects
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          {/* All Projects link */}
          <DropdownMenuItem
            data-testid="nav-projects-all"
            onClick={() => onNavigate("projects")}
            style={{ fontFamily: "'Manrope', sans-serif", cursor: "pointer" }}
            className="gap-2"
          >
            <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>⊞</span>
            <span style={{ color: "#374151", fontWeight: 600, fontSize: "0.82rem" }}>
              All Projects
            </span>
          </DropdownMenuItem>

          {projects.length > 0 && <DropdownMenuSeparator />}

          {/* Individual project items */}
          {projects.map((p) => (
            <DropdownMenuItem
              key={p.id}
              data-testid={`nav-project-${p.id}`}
              onClick={() => onOpenKanban(p)}
              style={{ fontFamily: "'Manrope', sans-serif", cursor: "pointer" }}
              className="gap-2"
            >
              <span style={{ fontSize: "1rem", lineHeight: 1, flexShrink: 0 }}>
                {p.emoji || "📁"}
              </span>
              <span style={{
                color: "#1A4D2E", fontSize: "0.82rem", fontWeight: 500,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {p.title}
              </span>
            </DropdownMenuItem>
          ))}

          {projects.length === 0 && (
            <div style={{
              padding: "0.5rem 0.75rem",
              fontFamily: "'Manrope', sans-serif",
              fontSize: "0.78rem", color: "#9ca3af",
            }}>
              No active projects
            </div>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AppSidebar({ open, activeView, isMobile, onNavigate, projects = [], onOpenKanban }) {

  /* ── Mobile: iOS-style sticky bottom tab bar ── */
  if (isMobile) {
    return (
      <nav
        data-testid="bottom-nav"
        style={{
          position: "fixed",
          bottom: 0, left: 0, right: 0,
          zIndex: 50,
          background: "rgba(255,255,255,0.90)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          borderTop: "1px solid rgba(255,255,255,0.55)",
          boxShadow: "0 -2px 20px rgba(26,77,46,0.10)",
          display: "flex",
          alignItems: "stretch",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        {NAV_ITEMS.map(({ id, Icon, label }) => {
          const active = activeView === id;
          return (
            <button
              key={id}
              data-testid={`nav-${id}`}
              onClick={() => onNavigate(id)}
              style={{
                flex: 1, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                gap: "3px", padding: "8px 4px 6px",
                background: "none", border: "none",
                color: active ? "#1A4D2E" : "#9ca3af",
                cursor: "pointer", transition: "color 0.15s",
                minWidth: 0, minHeight: "56px",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              <div style={{
                width: "44px", height: "28px", display: "flex",
                alignItems: "center", justifyContent: "center",
                borderRadius: "14px",
                background: active ? "rgba(26,77,46,0.12)" : "transparent",
                transition: "background 0.2s",
              }}>
                <Icon size={19} strokeWidth={active ? 2.5 : 1.8} style={{ transition: "stroke-width 0.15s" }} />
              </div>
              <span style={{
                fontSize: "0.6rem", fontFamily: "'Manrope', sans-serif",
                fontWeight: active ? 700 : 400, lineHeight: 1,
                letterSpacing: active ? "0.01em" : "0",
                transition: "font-weight 0.15s",
              }}>
                {label}
              </span>
            </button>
          );
        })}
      </nav>
    );
  }

  /* ── Desktop / tablet: collapsible left sidebar ── */
  return (
    <div
      data-testid="sidebar"
      style={{
        position: "fixed", left: 0, top: 0, height: "100vh",
        width: open ? "240px" : "56px",
        overflow: "hidden", zIndex: 40,
        background: "rgba(255, 255, 255, 0.28)",
        backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
        borderRight: "1px solid rgba(255, 255, 255, 0.45)",
        transition: "width 300ms ease",
      }}
    >
      <div style={{
        width: open ? "240px" : "56px",
        height: "100%", display: "flex", flexDirection: "column",
        paddingTop: "68px", transition: "width 300ms ease",
        overflowY: "auto", overflowX: "hidden",
      }}>
        {open ? (
          <div style={{
            padding: "10px 20px 8px",
            borderBottom: "1px solid rgba(26,77,46,0.1)", marginBottom: "4px",
          }}>
            <p style={{
              color: "#6b7280", fontSize: "0.65rem", fontFamily: "'Manrope', sans-serif",
              textTransform: "uppercase", letterSpacing: "0.1em", margin: 0,
            }}>
              Navigation
            </p>
          </div>
        ) : (
          <div style={{ height: "37px" }} />
        )}

        {NAV_ITEMS.slice(0, 5).map(({ id, Icon, label }) => {
          if (id === "projects") {
            return (
              <ProjectsDropdown
                key={id}
                open={open}
                active={activeView === "projects"}
                projects={projects}
                onNavigate={onNavigate}
                onOpenKanban={onOpenKanban || (() => {})}
              />
            );
          }
          return (
            <SidebarItem
              key={id} id={id} Icon={Icon} label={label}
              active={activeView === id} open={open}
              onClick={() => onNavigate(id)}
            />
          );
        })}

        <div style={{ height: "1px", background: "rgba(26,77,46,0.12)", margin: "6px 12px" }} />

        <SidebarItem
          id="settings" Icon={Settings} label="Settings"
          active={activeView === "settings"} open={open}
          onClick={() => onNavigate("settings")}
        />
      </div>
    </div>
  );
}
